import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Eye, CheckCircle2, Trash2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

const statusColor = {
  new: "bg-[#c97b3a] text-white",
  contacted: "bg-[#5a5245] text-white",
  booked: "bg-[#3d5a3d] text-white",
  declined: "bg-[#a34343] text-white",
};

export default function InquiriesTable({ refreshKey, onChange }) {
  const [inquiries, setInquiries] = useState([]);
  const [convertOpen, setConvertOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [price, setPrice] = useState(1200);

  const load = () => api.get("/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      toast.success("Updated");
      load(); onChange?.();
    } catch { toast.error("Update failed"); }
  };

  const removeInquiry = async (id) => {
    try {
      await api.delete(`/inquiries/${id}`);
      toast.success("Inquiry removed");
      load(); onChange?.();
    } catch { toast.error("Delete failed"); }
  };

  const convertToBooking = async () => {
    if (!active) return;
    try {
      await api.post("/bookings", {
        customer_name: active.name,
        customer_email: active.email,
        event_date: active.event_date,
        event_location: active.event_location,
        guest_count: active.guest_count,
        taps_needed: active.taps_needed,
        beverage_type: active.beverage_type,
        event_type: active.event_type,
        price: Number(price),
        notes: active.message,
        inquiry_id: active.id,
      });
      toast.success("Converted to booking!");
      setConvertOpen(false);
      setActive(null);
      load(); onChange?.();
    } catch { toast.error("Conversion failed"); }
  };

  return (
    <div className="paper-card overflow-hidden">
      <Table data-testid="inquiries-table">
        <TableHeader>
          <TableRow className="bg-[#e8dcc4]/40 hover:bg-[#e8dcc4]/40">
            <TableHead className="font-display text-[#1f2e1f]">Contact</TableHead>
            <TableHead className="font-display text-[#1f2e1f]">Event</TableHead>
            <TableHead className="font-display text-[#1f2e1f]">Details</TableHead>
            <TableHead className="font-display text-[#1f2e1f]">Status</TableHead>
            <TableHead className="font-display text-[#1f2e1f] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {inquiries.length === 0 && (
            <TableRow><TableCell colSpan={5} className="text-center text-[#8c8577] py-10">No inquiries yet</TableCell></TableRow>
          )}
          {inquiries.map((i) => (
            <TableRow key={i.id} data-testid={`inquiry-row-${i.id}`}>
              <TableCell>
                <div className="font-medium text-[#1f2e1f]">{i.name}</div>
                <div className="text-xs text-[#8c8577]">{i.email}</div>
                {i.phone && <div className="text-xs text-[#8c8577]">{i.phone}</div>}
              </TableCell>
              <TableCell>
                <div className="text-sm text-[#1f2e1f]">{format(parseISO(i.event_date), "MMM d, yyyy")}</div>
                <div className="text-xs text-[#8c8577]">{i.event_location}</div>
                <div className="text-xs text-[#c97b3a] uppercase tracking-wider">{i.event_type}</div>
              </TableCell>
              <TableCell>
                <div className="text-sm text-[#5a5245]">{i.guest_count} guests · {i.taps_needed} taps</div>
                <div className="text-xs text-[#8c8577]">{i.beverage_type}</div>
              </TableCell>
              <TableCell>
                <Select value={i.status} onValueChange={(v) => updateStatus(i.id, v)}>
                  <SelectTrigger className="w-32 h-8 text-xs" data-testid={`inquiry-status-${i.id}`}>
                    <Badge className={`${statusColor[i.status]} rounded-full text-[10px] uppercase`}>{i.status}</Badge>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="booked">Booked</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="ghost" data-testid={`inquiry-view-${i.id}`}><Eye className="h-4 w-4" /></Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="font-display text-2xl">{i.name}</DialogTitle>
                        <DialogDescription>{i.email} · {i.phone}</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-2 text-sm">
                        <p><span className="font-semibold">Event:</span> {i.event_type} on {format(parseISO(i.event_date), "PPP")}</p>
                        <p><span className="font-semibold">Location:</span> {i.event_location}</p>
                        <p><span className="font-semibold">Guests:</span> {i.guest_count}</p>
                        <p><span className="font-semibold">Setup:</span> {i.taps_needed} taps · {i.beverage_type}</p>
                        {i.message && <p className="border-t pt-2 mt-2 italic text-[#5a5245]">"{i.message}"</p>}
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button size="sm" variant="ghost" onClick={() => { setActive(i); setConvertOpen(true); setPrice(1200); }} data-testid={`inquiry-convert-${i.id}`} className="text-[#3d5a3d] hover:bg-[#3d5a3d]/10">
                    <CheckCircle2 className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => removeInquiry(i.id)} data-testid={`inquiry-delete-${i.id}`} className="text-[#a34343] hover:bg-[#a34343]/10">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Convert to Booking</DialogTitle>
            <DialogDescription>Lock in {active?.name}'s event with a final price.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Final Price ($)</Label>
            <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="convert-price-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertOpen(false)}>Cancel</Button>
            <Button className="btn-orange" onClick={convertToBooking} data-testid="convert-confirm-button">Create Booking</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
